import { finallyRule, uuidv7 } from './profilesDoc'
import type { CallOrder, CallRule, ProfilePool, ProfileWorkProfile, RuleCondition, RuleConditionKind } from './types'

// A work profile's calling chain, Orchestra's way (Part 2 §3, §13.2): rules[0] is
// Finally and calls from everything the profile serves, so its queueIds and pools
// stay empty; rules[1:] are the exceptions, read in order, each over a subset.

/** The orders in the order the editor lists them: Orchestra's first, ours after, then the two odd ones. */
export const ORDERS: readonly CallOrder[] = [
  'max_waiting',
  'defined',
  'max_lifetime',
  'service_level',
  'closest_to_target',
  'priority_then_waiting',
  'fair_share',
  'single',
  'do_not_call',
]

export const CONDITION_KINDS: readonly RuleConditionKind[] = [
  'wait_over',
  'over_service_level',
  'visits_count',
  'called_count',
  'time_between',
  'caller_is_primary',
  'no_primary',
  'appointment_late',
]

export type Slot = 'queue' | 'op' | 'n' | 'scope' | 'from' | 'until'

const SLOTS: Record<RuleConditionKind, Slot[]> = {
  wait_over: ['queue', 'n'],
  over_service_level: ['queue', 'n'],
  time_between: ['from', 'until'],
  visits_count: ['queue', 'op', 'n'],
  called_count: ['op', 'n', 'queue', 'scope'],
  caller_is_primary: ['queue'],
  no_primary: ['queue'],
  appointment_late: ['queue', 'n'],
}

/** The controls a condition's sentence holds. */
export function conditionSlots(kind: RuleConditionKind): Slot[] {
  return [...SLOTS[kind]]
}

/** Whether a condition looks at a queue (every kind but time of day). */
export function conditionHasQueue(kind: RuleConditionKind): boolean {
  return SLOTS[kind].includes('queue')
}

/** A condition of this kind with Orchestra-like starting values. */
export function newCondition(kind: RuleConditionKind, queueId: string): RuleCondition {
  const base: RuleCondition = { kind, n: 0, from: 0, until: 0 }
  if (conditionHasQueue(kind)) base.queueId = queueId
  switch (kind) {
    case 'wait_over':
    case 'appointment_late':
      return { ...base, n: 10 }
    case 'visits_count':
      return { ...base, op: 'gt', n: 5 }
    case 'called_count':
      return { ...base, op: 'lt', n: 3, scope: 'user' }
    case 'time_between':
      return { ...base, from: 720, until: 840 }
    default:
      return base
  }
}

const SLOT_NAMES = new Set<string>(['queue', 'op', 'n', 'scope', 'from', 'until'])

/** A condition sentence cut at its {slots}, in the language's own order, so each slot becomes a control. */
export function splitPhrase(text: string): Array<string | { slot: Slot }> {
  const parts: Array<string | { slot: Slot }> = []
  let last = 0
  for (const m of text.matchAll(/\{(\w+)\}/g)) {
    if (!SLOT_NAMES.has(m[1]!)) continue
    if (m.index > last) parts.push(text.slice(last, m.index))
    parts.push({ slot: m[1] as Slot })
    last = m.index + m[0].length
  }
  if (last < text.length) parts.push(text.slice(last))
  return parts
}

/** Finally lives with the other document helpers; re-exported for the rule editor. */
export { finallyRule }

/** A new exception: the profile's first queue, waited on too long. With no queue yet, time of day. */
export function exceptionRule(profileQueues: readonly string[]): CallRule {
  const first = profileQueues[0]
  return {
    id: uuidv7(),
    queueIds: first ? [first] : [],
    order: 'max_waiting',
    conditions: [first ? newCondition('wait_over', first) : newCondition('time_between', '')],
    primaryResource: 'ignore',
  }
}

function hasAny(rule: CallRule): boolean {
  return rule.queueIds.length > 0 || (rule.pools ?? []).length > 0
}

/** Drops from every rule what the profile no longer serves; an exception left with nothing is removed. */
function pruneRules(rules: readonly CallRule[], queues: ReadonlySet<string>, pools: ReadonlySet<ProfilePool>): CallRule[] {
  const out: CallRule[] = []
  rules.forEach((rule, i) => {
    const next: CallRule = { ...rule, queueIds: rule.queueIds.filter((q) => queues.has(q)) }
    if (rule.pools) next.pools = rule.pools.filter((p) => pools.has(p))
    if (rule.conditions) {
      next.conditions = rule.conditions.filter((c) => !conditionHasQueue(c.kind) || queues.has(c.queueId ?? ''))
    }
    if (rule.shares) {
      const kept = Object.entries(rule.shares).filter(([q]) => queues.has(q))
      if (kept.length > 0) next.shares = Object.fromEntries(kept)
      else delete next.shares
    }
    if (i > 0 && hasAny(rule) && !hasAny(next)) return
    out.push(next)
  })
  return out
}

export function setProfileQueues(wp: ProfileWorkProfile, next: string[]): ProfileWorkProfile {
  return {
    ...wp,
    queueIds: next,
    rules: pruneRules(wp.rules, new Set(next), new Set(wp.pools ?? [])),
  }
}

export function setProfilePools(wp: ProfileWorkProfile, next: ProfilePool[]): ProfileWorkProfile {
  return {
    ...wp,
    pools: next,
    rules: pruneRules(wp.rules, new Set(wp.queueIds), new Set(next)),
  }
}

/** Shares mean something only to fair_share. */
export function setRuleOrder(rule: CallRule, order: CallOrder): CallRule {
  const next: CallRule = { ...rule, order }
  if (order !== 'fair_share') delete next.shares
  return next
}

/** Reorders the exceptions; Finally stays first. */
export function moveRule(wp: ProfileWorkProfile, from: number, to: number): ProfileWorkProfile {
  const n = wp.rules.length
  if (from === to || from < 1 || to < 1 || from >= n || to >= n) return wp
  const rules = [...wp.rules]
  const [moved] = rules.splice(from, 1)
  rules.splice(to, 0, moved!)
  return { ...wp, rules }
}
